import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Line, Bar, Pie, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBox, 
  faDollarSign, 
  faFileAlt, 
  faFileCircleCheck, 
  faUsers, 
  faUniversity, 
  faMicrochip 
} from '@fortawesome/free-solid-svg-icons';

const ACCENT = "from-indigo-600 to-cyan-400"; // gradient
const BADGE = "bg-gradient-to-r from-indigo-500 to-cyan-400 text-white";

const useCases = [
  {
    id: "supply",
    title: "Supply Chain",
    icon: faBox,
    subtabs: [
      { id: "track", label: "Track Item" },
      { id: "provenance", label: "Provenance" },
      { id: "certify", label: "Certify Batch" },
    ],
  },
  {
    id: "finance",
    title: "Digital Financing",
    icon: faDollarSign,
    subtabs: [
      { id: "invoice", label: "Invoice Financing" },
      { id: "credit", label: "On-Chain Credit" },
      { id: "lenders", label: "Lenders" },
    ],
  },
  {
    id: "contracts",
    title: "Smart Contracts",
    icon: faFileAlt,
    subtabs: [
      { id: "create", label: "Create Contract" },
      { id: "active", label: "Active Contracts" },
      { id: "exec", label: "Execute / Test" },
    ],
  },
  {
    id: "compliance",
    title: "Compliance",
    icon: faFileCircleCheck,
    subtabs: [
      { id: "certs", label: "Certificates" },
      { id: "ip", label: "IP Timestamp" },
      { id: "audit", label: "Audit Logs" },
    ],
  },
  {
    id: "coop",
    title: "Cooperative",
    icon: faUsers,
    subtabs: [
      { id: "dashboard", label: "Co-op Dashboard" },
      { id: "tokens", label: "Token Distribution" },
      { id: "voting", label: "Voting" },
    ],
  },
  {
    id: "gov",
    title: "Public Sector",
    icon: faUniversity,
    subtabs: [
      { id: "registry", label: "Subsidy Registry" },
      { id: "claims", label: "Scheme Claims" },
      { id: "verify", label: "Beneficiary Verify" },
    ],
  },
  {
    id: "aiot",
    title: "AI / IoT",
    icon: faMicrochip,
    subtabs: [
      { id: "feeds", label: "IoT Feeds" },
      { id: "predict", label: "Predictive Insights" },
      { id: "maintenance", label: "Maintenance" },
    ],
  },
];

// ---------- Mock data (replace with backend) ----------
const MOCK_INVOICES = [
  { id: "INV-001", buyer: "RetailCo", amount: 50000, funded: 30000, date: "2025-07-10" },
  { id: "INV-002", buyer: "CafeBrew", amount: 20000, funded: 0, date: "2025-08-02" },
  { id: "INV-003", buyer: "GroceryPlus", amount: 75000, funded: 75000, date: "2025-08-18" },
];

const MOCK_SHIPMENTS = [
  { id: "SHIP-001", product: "Spices Batch A", status: "In Transit", timeline: ["Packed", "Dispatched", "In Transit"] },
  { id: "SHIP-002", product: "Label Rolls", status: "Delivered", timeline: ["Packed", "Dispatched", "Delivered"] },
];

const MOCK_CONTRACTS = [
  { id: "CTR-101", title: "Supply Agreement - RetailCo", value: 120000, status: "Active" },
  { id: "CTR-102", title: "Maintenance SLA - PackMach", value: 40000, status: "Pending" },
];

const MOCK_CERTS = [
  { id: "CERT-11", name: "FSSAI-2025", issuer: "FSSAI", validTill: "2026-04-01" },
  { id: "CERT-22", name: "ISO-9001", issuer: "QMS Labs", validTill: "2027-01-30" },
];

const MOCK_COOP = {
  members: [
    { id: "M1", name: "Anita", share: 30 },
    { id: "M2", name: "Rahul", share: 25 },
    { id: "M3", name: "Kavita", share: 20 },
    { id: "M4", name: "Arjun", share: 25 },
  ],
  proposals: [
    { id: "P1", title: "Buy new label machine", votesFor: 70, votesAgainst: 30 },
  ],
};

const MOCK_SUBSIDIES = [
  { id: "BEN-001", name: "Suresh", scheme: "Energy Subsidy", status: "Approved" },
  { id: "BEN-002", name: "Meera", scheme: "Capital Support", status: "Pending" },
];

const MOCK_IOT = [
  { id: "S1", label: "ColdRoom-1", metric: 4.2, unit: "°C", updated: "2m ago" },
  { id: "S2", label: "Mixer-Temp", metric: 62, unit: "°C", updated: "10m ago" },
];

// helper utility to simulate async calls (replace in integration)
const simulateApi = (result, delay = 600) =>
  new Promise((resolve) => setTimeout(() => resolve(result), delay));

const generateHash = (str) => {
  // rudimentary hash simulation --- DO NOT use for production; integrate real hashing (ethers.utils.keccak256) later
  let s = `${str}-${Date.now()}-${Math.random()}`;
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash << 5) - hash + s.charCodeAt(i);
  return `0x${(hash >>> 0).toString(16).padStart(8, "0")}`;
};

// ---------- Small presentational components ----------
function IconTab({ icon, label, active }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer min-w-[7rem] md:min-w-[11rem] transition-shadow ${active ? "shadow-lg bg-indigo-700" : "bg-white/5"}`}>
      <div className={`p-1 rounded-md ${active ? "bg-white/10" : "bg-white/5"}`}>
        <FontAwesomeIcon icon={icon} size="lg" />
      </div>
      <div className="text-sm font-semibold truncate">{label}</div>
    </div>
  );
}

function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-md text-sm font-medium whitespace-nowrap ${
        active ? "text-white bg-indigo-600/90" : "text-gray-300 bg-white/5"
      }`}
    >
      {label}
    </button>
  );
}

// ---------- Chart helper components ----------
function InvoiceBarChart({ data }) {
  const chartData = {
    labels: data.map(d => d.id),
    datasets: [
      {
        label: 'Amount',
        data: data.map(d => d.amount),
        backgroundColor: '#6366f1',
        barThickness: 14,
      },
      {
        label: 'Funded',
        data: data.map(d => d.funded),
        backgroundColor: '#06b6d4',
        barThickness: 8,
      },
    ],
  };

  const options = { responsive: true, plugins: { legend: { position: 'top' } } };

  return (
    <div className="mt-2" style={{ width: '100%', height: 200 }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}


function TrustScoreBar({ data }) {
  const chartData = {
    labels: data.map(d => d.name),
    datasets: [
      {
        label: 'Trust Score',
        data: data.map(d => d.score),
        backgroundColor: '#10b981',
        barThickness: 18,
      },
    ],
  };

  const options = {
    indexAxis: 'y', // horizontal bars
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, max: 100 } },
  };

  return (
    <div className="mt-2" style={{ width: '100%', height: 220 }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}


function PieDistribution({ data }) {
  const COLORS = ['#6366f1', '#06b6d4', '#7c3aed', '#10b981', '#f59e0b'];

  const chartData = {
    labels: data.map(d => d.name),
    datasets: [{
      data: data.map(d => d.value),
      backgroundColor: COLORS,
      hoverOffset: 8,
    }],
  };

  const options = { responsive: true, plugins: { legend: { position: 'right', labels: { boxWidth: 12 } } } };

  return (
    <div className="mt-2" style={{ width: '100%', height: 200 }}>
      <Pie data={chartData} options={options} />
    </div>
  );
}

function PredictivePanel() {
  const data = [
    { name: "May", value: 20 }, { name: "Jun", value: 30 }, 
    { name: "Jul", value: 24 }, { name: "Aug", value: 38 }, 
    { name: "Sep", value: 42 }, { name: "Oct", value: 55 }
  ];

  const chartData = {
    labels: data.map(d => d.name),
    datasets: [
      {
        label: 'Predicted Sales',
        data: data.map(d => d.value),
        borderColor: '#06b6d4',
        backgroundColor: '#06b6d4',
        fill: false,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } },
  };

  return (
    <div>
      <div className="text-sm text-gray-300">Predictive sales / demand (sample)</div>
      <div className="mt-2" style={{ width: '100%', height: 220 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}


// ---------- Main component ----------
export default function TrustChainHome() {
  const [activeUse, setActiveUse] = useState(useCases[0].id);
  const [activeSub, setActiveSub] = useState(useCases[0].subtabs[0].id);

  // Data states (simulate fetch)
  const [invoices, setInvoices] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [certs, setCerts] = useState([]);
  const [coop, setCoop] = useState(MOCK_COOP);
  const [subsidies, setSubsidies] = useState([]);
  const [iot, setIot] = useState([]);

  useEffect(() => {
    // simulate initial fetches
    simulateApi(MOCK_INVOICES).then(setInvoices);
    simulateApi(MOCK_SHIPMENTS).then(setShipments);
    simulateApi(MOCK_CONTRACTS).then(setContracts);
    simulateApi(MOCK_CERTS).then(setCerts);
    simulateApi(MOCK_SUBSIDIES).then(setSubsidies);
    simulateApi(MOCK_IOT).then(setIot);
  }, []);

  useEffect(() => {
    // keep subtab in sync when main tab changes
    const current = useCases.find((u) => u.id === activeUse);
    if (current) setActiveSub(current.subtabs[0].id);
  }, [activeUse]);

  // ---------- Handlers (replace with real API/web3 integration) ----------
const handleTrack = async (trackingId) => {
  // TODO: INTEGRATE with on-chain shipment proof lookup or smart contract query
  // Simulated local lookup for now
  const simulated = shipments.find((s) => s.id === trackingId);

  if (simulated) {
    alert(`🛰️ Tracking ID: ${trackingId}\nStatus: ${simulated.status}\nLocation: ${simulated.location}`);
  } else {
    alert("⚠️ Tracking ID not found on blockchain ledger.");
  }
};

  // For brevity in the UI code below we use inline small helpers where needed

  // A small TrustScore builder from invoices/funded ratio (demo)
  const computeTrustScores = (invoicesList) => {
    // produce scores 0-100 based on funding & frequency
    const map = {};
    invoicesList.forEach((inv) => {
      const name = inv.buyer;
      map[name] = map[name] || { name, total: 0, funded: 0, count: 0 };
      map[name].total += inv.amount;
      map[name].funded += inv.funded;
      map[name].count += 1;
    });
    return Object.values(map).map((m) => ({ name: m.name, score: Math.round((m.funded / Math.max(1, m.total)) * 100) }));
  };

  // ---------- Renderers for each subtab ----------
  function renderSubContent(useId, subId) {
    // Supply Chain
    if (useId === "supply") {
      if (subId === "track") {
        return <TrackItemPanel shipments={shipments} />;
      }
      if (subId === "provenance") {
        return <ProvenancePanel shipments={shipments} />;
      }
      if (subId === "certify") {
        return <CertifyBatchPanel onSave={(c) => setCerts((s) => [c, ...s])} />;
      }
    }

    // Financing
    if (useId === "finance") {
      if (subId === "invoice") {
        return <InvoicePanel invoices={invoices} onAdd={(inv) => setInvoices((s) => [inv, ...s])} />;
      }
      if (subId === "credit") {
        return <CreditPanel scores={computeTrustScores(invoices)} />;
      }
      if (subId === "lenders") {
        return <LendersPanel />;
      }
    }

    // Smart Contracts
    if (useId === "contracts") {
      if (subId === "create") {
        return <CreateContract onCreate={(c) => setContracts((s) => [c, ...s])} />;
      }
      if (subId === "active") {
        return <ActiveContractsPanel contracts={contracts} />;
      }
      if (subId === "exec") {
        return <ExecuteContractPanel contracts={contracts} />;
      }
    }

    // Compliance
    if (useId === "compliance") {
      if (subId === "certs") {
        return <CertificatesPanel certs={certs} onVerify={(id) => alert(`Verify ${id} (connect to chain)`)} />;
      }
      if (subId === "ip") {
        return <IpTimestampPanel onStamp={(r) => setCerts((s) => [r, ...s])} />;
      }
      if (subId === "audit") {
        return <AuditTrailPanel />;
      }
    }

    // Cooperative
    if (useId === "coop") {
      if (subId === "dashboard") {
        return <CoopDashboardPanel coop={coop} />;
      }
      if (subId === "tokens") {
        return <TokenDistributionPanel coop={coop} />;
      }
      if (subId === "voting") {
        return <VotingPanel coop={coop} onVote={(id, vote) => alert(`Vote ${vote} on ${id}`)} />;
      }
    }

    // Government
    if (useId === "gov") {
      if (subId === "registry") {
        return <SubsidyRegistryPanel subsidies={subsidies} />;
      }
      if (subId === "claims") {
        return <SchemeClaimsPanel onClaim={(c) => setSubsidies((s) => [c, ...s])} />;
      }
      if (subId === "verify") {
        return <BeneficiaryVerifyPanel subsidies={subsidies} />;
      }
    }

    // AI/IoT
    if (useId === "aiot") {
      if (subId === "feeds") {
        return <IoTFeedsPanel iot={iot} />;
      }
      if (subId === "predict") {
        return <PredictivePanel />;
      }
      if (subId === "maintenance") {
        return <MaintenancePanel />;
      }
    }

    return <div className="text-sm text-gray-300">No view available</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b to-slate-900 from-slate-800 text-white p-4 sm:p-6">
      <div className="mx-auto">
        <header className="flex items-center justify-between gap-3 mb-4">
          <div>
            {/* <div className="text-xs uppercase text-indigo-300">Think5Whys TrustChain</div> */}
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">Think5Whys TrustChain — Blockchain Applications for MSMEs</h1>
          </div>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-8 p-4">
        {useCases.map((u) => (
            <div
            key={u.id}
            onClick={() => setActiveUse(u.id)}
            className={`cursor-pointer transition-transform transform hover:scale-105`}
            >
            <IconTab Icon={u.icon} label={u.title} active={activeUse === u.id} />
            </div>
        ))}
        </div>

        <div className="mt-3 bg-white/3 rounded-xl p-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 flex-1 bg-gray-600 px-8 py-4">
            {useCases.find((x) => x.id === activeUse).subtabs.map((s) => (
            <div key={s.id} onClick={() => setActiveSub(s.id)} className="cursor-pointer">
                <SubTab label={s.label} active={activeSub === s.id} />
            </div>
            ))}
          </div>

          <div className="mt-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeUse}-${activeSub}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
              >
                {renderSubContent(activeUse, activeSub)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Footer summary */}
        <footer className="mt-6 text-xs text-gray-400">@2025 Think5Whys Golbal DeepTech Solutions, Mumbai</footer>
      </div>
    </div>
  );
}

// ========== Subpanel implementations (kept below for organization) ==========

function TrackItemPanel({ shipments }) {
  const [id, setId] = useState("");
  const [result, setResult] = useState(null);

  const handleLookup = async () => {
    // TODO: INTEGRATE with on-chain lookup
    const found = shipments.find((s) => s.id.toLowerCase() === id.toLowerCase());
    const res = await simulateApi(found || null, 400);
    setResult(res);
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={id}
          onChange={(e) => setId(e.target.value)}
          className="bg-white/5 rounded-md px-3 py-2 text-sm"
          style={{width: "300px"}}
          placeholder="Enter tracking ID e.g. SHIP-001"
        />
        <button onClick={handleLookup} className="px-3 py-2 w-20 rounded-md bg-indigo-600 font-semibold">Lookup</button>
      </div>

      <div className="mt-4">
        {!result && <div className="text-sm text-gray-300">Enter ID and press Lookup to see on-chain timeline & proofs.</div>}
        {result && (
          <div className="mt-2 bg-white/5 p-3 rounded-md">
            <div className="font-semibold">{result.product} — {result.id}</div>
            <div className="text-xs text-gray-300 mt-2">Status: <span className="font-semibold">{result.status}</span></div>
            <ol className="mt-3 text-sm space-y-2">
              {result.timeline.map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2" />
                  <div>
                    <div className="text-sm">{t}</div>
                    <div className="text-xs text-gray-400">Proof: {generateHash(result.id + t)}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

function ProvenancePanel({ shipments }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Provenance records (mock). Click Verify to compute a proof hash.</div>
      <div className="mt-3 space-y-3">
        {shipments.map((s) => (
          <div key={s.id} className="p-3 bg-white/5 rounded-md">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">{s.product}</div>
                <div className="text-xs text-gray-300">{s.id} • {s.status}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-400">Proof:</div>
                <div className="text-xs font-mono text-sm bg-white/5 px-2 py-1 rounded">{generateHash(s.id)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertifyBatchPanel({ onSave }) {
  const [batch, setBatch] = useState("");
  const [issuer, setIssuer] = useState("");
  const [validTill, setValidTill] = useState("");
  const [hash, setHash] = useState(null);

  const handleCertify = async () => {
    // TODO: INTEGRATE with on-chain certificate minting/timestamp
    const h = generateHash(batch + issuer + validTill);
    const cert = { id: `CERT-${Math.floor(Math.random() * 9999)}`, name: batch, issuer, validTill };
    onSave(cert);
    setHash(h);
  };

  return (
    <div className="space-y-3">
      <input className="w-full bg-white/5 rounded px-3 py-2 text-sm" placeholder="Batch name" value={batch} onChange={(e) => setBatch(e.target.value)} />
      <input className="w-full bg-white/5 rounded px-3 py-2 text-sm" placeholder="Issuer (eg. FSSAI)" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
      <input type="date" className="w-full bg-white/5 rounded px-3 py-2 text-sm" value={validTill} onChange={(e) => setValidTill(e.target.value)} />
      <div className="flex flex-row gap-2">
        <button onClick={handleCertify} className="w-40 px-3 py-2 bg-cyan-500 rounded-md font-semibold">Issue Certificate</button>
        <button onClick={() => { setBatch(""); setIssuer(""); setValidTill(""); }} className="w-20 px-3 py-2 rounded-md bg-white/5">Clear</button>
      </div>
      {hash && <div className="text-xs text-gray-300">Issued on-chain hash: <span className="font-mono text-sm ml-2">{hash}</span></div>}
    </div>
  );
}

function InvoicePanel({ invoices, onAdd }) {
  const [buyer, setBuyer] = useState("");
  const [amount, setAmount] = useState("");

  const handleAdd = () => {
    const inv = { id: `INV-${Math.floor(Math.random() * 900) + 100}`, buyer, amount: Number(amount), funded: 0, date: new Date().toISOString().slice(0, 10) };
    // TODO: INTEGRATE create-invoice => record invoice hash on chain
    onAdd(inv);
    setBuyer(""); setAmount("");
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-2">
        <input placeholder="Buyer name" value={buyer} onChange={(e) => setBuyer(e.target.value)} className="bg-white/5 rounded px-3 py-2 text-sm" />
        <input placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-white/5 rounded px-3 py-2 text-sm" />
        <div className="flex gap-2">
          <button onClick={handleAdd} className="w-40 bg-indigo-600 rounded px-3 py-2 font-semibold">Create Invoice</button>
        </div>
      </div>

      <div className="mt-3 text-sm text-gray-300">Recent invoices</div>
      <div className="mt-2 space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{inv.id} • {inv.buyer}</div>
              <div className="text-xs text-gray-300">Amount: ₹{inv.amount} • Funded: ₹{inv.funded}</div>
            </div>
            <div>
              <button className="px-3 py-1 rounded-md bg-white/5 text-sm">Request Discount</button>
            </div>
          </div>
        ))}
      </div>

      <InvoiceBarChart data={invoices} />
    </div>
  );
}

function CreditPanel({ scores }) {
  return (
    <div>
      <div className="text-sm text-gray-300">On-chain credit visibility (demo)</div>
      <TrustScoreBar data={scores} />
      <div className="mt-3 text-xs text-gray-400">Tip: Integrate payment history, invoice settlement proofs & gov records to improve scores.</div>
    </div>
  );
}

function LendersPanel() {
  const lenders = [
    { id: "L1", name: "FinBuddy", rate: "12%", max: 200000 },
    { id: "L2", name: "QuickFund", rate: "15%", max: 100000 },
  ];
  return (
    <div>
      <div className="text-sm text-gray-300">Connected lenders (mock). Share invoice hash to request offers.</div>
      <div className="mt-3 space-y-2">
        {lenders.map((l) => (
          <div key={l.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{l.name}</div>
              <div className="text-xs text-gray-300">Rate: {l.rate} • Max: ₹{l.max}</div>
            </div>
            <div>
              <button className="px-3 py-1 rounded-md bg-cyan-500 w-40">Request Offer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateContract({ onCreate }) {
  const [title, setTitle] = useState("");
  const [partyA, setPartyA] = useState("");
  const [partyB, setPartyB] = useState("");
  const [value, setValue] = useState("");
  const [hash, setHash] = useState(null);

  const handleCreate = async () => {
    // TODO: INTEGRATE with smart contract deployment / template store
    const h = generateHash(title + partyA + partyB + value);
    const contract = { id: `CTR-${Math.floor(Math.random() * 900) + 100}`, title, value: Number(value), status: "Draft" };
    onCreate(contract);
    setHash(h);
  };

  return (
    <div className="space-y-3">
      <input className="w-full bg-white/5 rounded px-3 py-2" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <input className="bg-white/5 rounded px-3 py-2" placeholder="Party A" value={partyA} onChange={(e) => setPartyA(e.target.value)} />
        <input className="bg-white/5 rounded px-3 py-2" placeholder="Party B" value={partyB} onChange={(e) => setPartyB(e.target.value)} />
      </div>
      <input className="w-full bg-white/5 rounded px-3 py-2" placeholder="Value (₹)" value={value} onChange={(e) => setValue(e.target.value)} />
      <div className="flex gap-2">
        <button onClick={handleCreate} className="w-40 bg-indigo-600 px-3 py-2 rounded-md font-semibold">Create on-chain</button>
        <button onClick={() => { setTitle(""); setPartyA(""); setPartyB(""); setValue(""); }} className="w-20 px-3 py-2 bg-white/5 rounded-md">Clear</button>
      </div>

      {hash && <div className="text-xs text-gray-300">Contract hash: <span className="font-mono ml-2">{hash}</span></div>}
    </div>
  );
}

function ActiveContractsPanel({ contracts }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Active & recent contracts recorded on-chain (mock).</div>
      <div className="mt-3 space-y-2">
        {contracts.map((c) => (
          <div key={c.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{c.title}</div>
              <div className="text-xs text-gray-300">{c.id} • ₹{c.value} • {c.status}</div>
            </div>
            <div>
              <button className="px-3 py-1 rounded-md bg-white/5">View</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExecuteContractPanel({ contracts }) {
  const [selected, setSelected] = useState(null);
  const [log, setLog] = useState("");

  const handleExecute = async () => {
    // TODO: Use ethers.js to call contract function (simulate here)
    if (!selected) return;
    setLog("Executing... (simulated)");
    await simulateApi(true, 700);
    setLog("Execution recorded on-chain: " + generateHash(selected.id));
  };

  return (
    <div>
      <select className="bg-white/5 rounded px-3 py-2 w-full" onChange={(e) => setSelected(contracts.find(c => c.id===e.target.value))}>
        <option value="">Select contract to execute</option>
        {contracts.map((c) => <option key={c.id} value={c.id}>{c.id} • {c.title}</option>)}
      </select>
      <div className="mt-3 flex gap-2">
        <button onClick={handleExecute} className="w-20 bg-cyan-500 px-3 py-2 rounded-md">Execute</button>
      </div>
      <div className="mt-3 text-xs text-gray-300 font-mono">{log}</div>
    </div>
  );
}

function CertificatesPanel({ certs, onVerify }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Certificates issued & recorded on-chain.</div>
      <div className="mt-3 space-y-2">
        {certs.map((c) => (
          <div key={c.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-gray-300">{c.issuer} • Valid till {c.validTill}</div>
            </div>
            <div>
              <button onClick={() => onVerify(c.id)} className="px-3 py-1 rounded-md bg-indigo-600">Verify</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IpTimestampPanel({ onStamp }) {
  const [docTitle, setDocTitle] = useState("");
  const [hash, setHash] = useState(null);

  const handleStamp = () => {
    const h = generateHash(docTitle);
    const record = { id: `CERT-${Math.floor(Math.random()*10000)}`, name: docTitle, issuer: "Self", validTill: "N/A" };
    onStamp(record);
    setHash(h);
  };

  return (
    <div>
      <input className="w-full bg-white/5 rounded px-3 py-2" placeholder="Document / Design title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} />
      <div className="mt-2 flex gap-2">
        <button onClick={handleStamp} className="w-40 bg-indigo-600 px-3 py-2 rounded-md">Timestamp on-chain</button>
      </div>
      {hash && <div className="mt-2 text-xs text-gray-300">Timestamp hash: <span className="font-mono">{hash}</span></div>}
    </div>
  );
}

function AuditTrailPanel() {
  const events = [
    { t: "2025-09-02", msg: "Invoice INV-001 funded by FinBuddy" },
    { t: "2025-08-18", msg: "Shipment SHIP-002 delivered" },
    { t: "2025-07-10", msg: "Certificate FSSAI issued" },
  ];
  return (
    <div>
      <div className="text-sm text-gray-300">Immutable audit timeline (combined on-chain proofs)</div>
      <ol className="mt-3 space-y-3">
        {events.map((e, idx) => (
          <li key={idx} className="p-3 bg-white/5 rounded-md">
            <div className="text-xs text-gray-400">{e.t}</div>
            <div className="font-medium">{e.msg}</div>
            <div className="text-xs text-gray-400 mt-1">Proof: {generateHash(e.t + e.msg)}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function CoopDashboardPanel({ coop }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Co-op members and their shares</div>
      <div className="mt-3 space-y-2">
        {coop.members.map((m) => (
          <div key={m.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{m.name}</div>
              <div className="text-xs text-gray-300">Share: {m.share}%</div>
            </div>
            <div className="text-xs text-gray-300">Proof: {generateHash(m.id)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TokenDistributionPanel({ coop }) {
  const data = coop.members.map((m) => ({ name: m.name, value: m.share }));
  return (
    <div>
      <div className="text-sm text-gray-300">Tokenized share distribution (visual)</div>
      <PieDistribution data={data} />
    </div>
  );
}

function VotingPanel({ coop, onVote }) {
  const [selected, setSelected] = useState(coop.proposals[0]?.id || null);
  return (
    <div>
      <div className="text-sm text-gray-300">Open proposals</div>
      <div className="mt-3 space-y-2">
        {coop.proposals.map((p) => (
          <div key={p.id} className="p-3 bg-white/5 rounded-md">
            <div className="font-medium">{p.title}</div>
            <div className="text-xs text-gray-300">{p.votesFor}% in favour</div>
            <div className="mt-2 flex gap-2">
              <button onClick={() => onVote(p.id, "for")} className="flex-1 bg-indigo-600 px-3 py-2 rounded-md">Vote For</button>
              <button onClick={() => onVote(p.id, "against")} className="flex-1 bg-white/5 px-3 py-2 rounded-md">Against</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubsidyRegistryPanel({ subsidies }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Beneficiaries & subsidy status (mock)</div>
      <div className="mt-3 space-y-2">
        {subsidies.map((s) => (
          <div key={s.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-gray-300">{s.scheme}</div>
            </div>
            <div className="text-sm">{s.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SchemeClaimsPanel({ onClaim }) {
  const [name, setName] = useState("");
  const [scheme, setScheme] = useState("");

  const handleClaim = () => {
    const c = { id: `BEN-${Math.floor(Math.random()*10000)}`, name, scheme, status: "Pending" };
    onClaim(c);
    setName(""); setScheme("");
  };

  return (
    <div>
      <input className="w-full bg-white/5 rounded px-3 py-2" placeholder="Applicant name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="w-full bg-white/5 rounded px-3 py-2 mt-2" placeholder="Scheme" value={scheme} onChange={(e) => setScheme(e.target.value)} />
      <div className="mt-2">
        <button onClick={handleClaim} className="bg-cyan-500 px-3 py-2 rounded-md w-full">Submit Claim</button>
      </div>
    </div>
  );
}

function BeneficiaryVerifyPanel({ subsidies }) {
  const [id, setId] = useState("");
  const [res, setRes] = useState(null);
  const verify = async () => {
    const found = subsidies.find((s) => s.id === id);
    const r = await simulateApi(found || null);
    setRes(r);
  };

  return (
    <div>
      <input className="w-full bg-white/5 rounded px-3 py-2" placeholder="Beneficiary ID e.g. BEN-001" value={id} onChange={(e) => setId(e.target.value)} />
      <div className="mt-2">
        <button onClick={verify} className="bg-indigo-600 px-3 py-2 rounded-md w-full">Verify</button>
      </div>
      {res && (
        <div className="mt-3 p-3 bg-white/5 rounded">
          <div className="font-medium">{res.name}</div>
          <div className="text-xs text-gray-300">{res.scheme} • {res.status}</div>
          <div className="text-xs text-gray-400 mt-2">On-chain proof: {generateHash(res.id)}</div>
        </div>
      )}
    </div>
  );
}

function IoTFeedsPanel({ iot }) {
  return (
    <div>
      <div className="text-sm text-gray-300">Live sensor feeds (sample). Hook your IoT gateway to push signed sensor data to the ledger.</div>
      <div className="mt-3 space-y-2">
        {iot.map((s) => (
          <div key={s.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{s.label}</div>
              <div className="text-xs text-gray-300">{s.updated}</div>
            </div>
            <div className="text-sm font-mono">{s.metric}{s.unit}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaintenancePanel() {
  const contracts = [{ id: "M1", name: "Compressor Service", nextDue: "2025-10-20" }];
  return (
    <div>
      <div className="text-sm text-gray-300">Maintenance contracts & automated triggers. Link machinery telemetry to trigger payments/SLAs on-chain.</div>
      <div className="mt-3 space-y-2">
        {contracts.map((c) => (
          <div key={c.id} className="p-3 bg-white/5 rounded-md flex items-center justify-between">
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-gray-300">Next due: {c.nextDue}</div>
            </div>
            <div>
              <button className="px-3 py-1 rounded-md bg-indigo-600">View SLA</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
